import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {useGetAriaLabelFromUrl} from '../../../hooks/useGetAriaLabelFromUrl/useGetAriaLabelFromUrl';
import {Text} from '../../atoms/Text/Text';
import {UnstyledLinkLinaria} from '../../atoms/UnstyledLink/UnstyledLinkLinaria';
import {ProductImage} from '../../molecules/ProductImage/ProductImage';
import {SlideFullWidthImageOrVideo} from './SlideFullWidthImageOrVideo';
import {SlideAmbientProps} from './types';

export const SlideAmbient: FC<SlideAmbientProps> = ({
  'data-testid': dataTestId = 'slideAmbient',
  bannerTarget,
  slideImage,
  slideVideo,
  isActive,
  isCloseToViewport,
  onSlideClick,
  className,
  headline,
  subline,
  productImages,
  floatingChip,
  brandLogo,
  customSlideImage,
  slideContentLabels,
  floatingTopRight,
  floatingTopLeft,
  onStopAutomaticSlide,
}) => {
  const getAriaLabelFromUrl = useGetAriaLabelFromUrl();

  return (
    <CardContainer
      className={className}
      data-testid={dataTestId}
      onClick={e => {
        e.preventDefault();
        bannerTarget.onClick?.();
        onSlideClick?.(e);
      }}
      data-active={isActive}
      to={bannerTarget.url}
      aria-label={getAriaLabelFromUrl(bannerTarget.url)}
    >
      <Card>
        {(floatingTopRight || floatingTopLeft) && (
          <FloatingTopElements>
            {floatingTopLeft}
            {floatingTopRight}
          </FloatingTopElements>
        )}

        <ImageContainer onTouchStart={onStopAutomaticSlide}>
          <StyledSlideFullWidthImageOrVideo
            data-testid="browseCardImage"
            customSlideImage={customSlideImage}
            slideImage={slideImage}
            slideVideo={slideVideo}
            isCloseToViewport={isCloseToViewport}
          />

          {floatingChip && <TopChipWrapper>{floatingChip}</TopChipWrapper>}

          {(brandLogo || productImages || slideContentLabels) && (
            <SlideContentWrapper>
              {slideContentLabels && (
                <SlideContentLabelsWrapper>
                  {slideContentLabels}
                </SlideContentLabelsWrapper>
              )}
              {brandLogo}
              <ProductsWrapper>
                {productImages?.map((productImage, index) => (
                  <StyledProductImage
                    src={productImage.url}
                    widths={[120, 240, 360]}
                    key={`spotlight-slider-product-${index}`}
                    alt={productImage.description}
                    increasePadding={true}
                    padded={true}
                  />
                ))}
              </ProductsWrapper>
            </SlideContentWrapper>
          )}

          {bannerTarget.url && <BannerTarget data-testid="bannerTarget" />}
        </ImageContainer>

        {(subline || headline) && (
          <HeadlinesWrapper>
            {subline && <Subline>{subline}</Subline>}
            {headline && <Headline>{headline}</Headline>}
          </HeadlinesWrapper>
        )}
      </Card>
    </CardContainer>
  );
};

const CardContainer = styled(UnstyledLinkLinaria)`
  display: block;
  height: 100%;
  position: relative;
  padding-top: 13px;
  list-style: none;
`;

const TopChipWrapper = styled.div`
  position: absolute;
  top: -11px;
  left: 0;
  right: 0;
  justify-items: center;
`;

const FloatingTopElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
`;

const ImageContainer = styled.div`
  position: relative;
`;

const Card = styled.div`
  position: relative;
  z-index: 1;
  aspect-ratio: 1;

  @keyframes cover-flow {
    0%,
    100% {
      transform: scaleY(0.85);
    }
    50% {
      transform: scaleY(1);
    }
  }

  @supports (view-timeline-axis: inline) {
    --timeline-name: --cover;
    view-timeline: var(--timeline-name);
    will-change: transform, opacity;
    view-timeline-axis: inline;
    animation: cover-flow both linear;
    animation-timeline: var(--timeline-name);
  }

  @supports not (view-timeline-axis: inline) {
    transform: scaleY(0.85);
    opacity: 0.4;
    transition: transform 0.3s ease-out, opacity 0.3s ease-out;
    will-change: transform;

    [data-active='true'] & {
      transform: scaleY(1);
      opacity: 1;

      > * {
        transition: transform 0.3s ease-out, opacity 0.3s ease-out;
        transform: scaleY(1);
        opacity: 1;
      }
    }

    [data-active='false'] & {
      transform: scaleY(0.85);
      opacity: 0.4;

      > * {
        transition: transform 0.3s ease-out, opacity 0.3s ease-out;
        transform: scaleY(1.15);
        opacity: 1;
      }
    }
  }
`;

const StyledSlideFullWidthImageOrVideo = styled(SlideFullWidthImageOrVideo)`
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 1;
  background-color: ${COLORS['Fill.Black']};
  box-shadow: 0.75px 4.5px 6px 0 rgba(0, 0, 0, 0.08),
    0 1.5px 12px 6px rgba(0, 0, 0, 0.07);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0.08%,
      rgba(0, 0, 0, 0.04) 25.61%,
      rgba(0, 0, 0, 0.15) 41.99%,
      rgba(0, 0, 0, 0.4) 96.1%
    );
  }
`;

const BannerTarget = styled.div`
  position: absolute;
  inset: -11px 0 0 0;
  z-index: 1;
`;

const HeadlinesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
  margin-top: 8px;
`;

const Headline = styled(Text)`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.42;
  color: ${COLORS['Text.White']};
`;

const Subline = styled(Text)`
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.5;
  color: ${COLORS['Text.White']};
  text-transform: uppercase;
`;

const SlideContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  bottom: 0;
  width: 100%;
  justify-content: center;
  text-align: center;
`;

const ProductsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 8px 8px;
  gap: 2px;
`;

const StyledProductImage = styled(ProductImage)`
  aspect-ratio: 3 / 4;
  height: 100%;
  padding-top: 0;

  &&& img {
    padding: 14% 12%;
  }
`;

const SlideContentLabelsWrapper = styled.div`
  padding-inline: 24px;
`;
